import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import { CalendarIcon, UserIcon, TagIcon, ChevronLeftIcon, ShareIcon, BookmarkIcon, FacebookIcon, TwitterIcon, LinkedinIcon, MailIcon, ArrowRightIcon, PlayIcon, ClockIcon, MessageSquareIcon, ThumbsUpIcon, DownloadIcon, FileTextIcon } from 'lucide-react';
export const VideoPage = () => {
  const {
    videoId
  } = useParams();
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedVideos, setRelatedVideos] = useState([]);
  const [showTranscript, setShowTranscript] = useState(false);
  useEffect(() => {
    // Simulate fetching video data
    const fetchVideo = () => {
      setLoading(true);
      // Mock videos data - in a real app, this would be fetched from an API
      const mockVideos = [{
        id: 'probiotics-hype',
        title: 'Ukweli Talks: Probiotics Beyond the Hype',
        type: 'Video',
        date: 'December 8, 2023',
        author: 'Dr. James Wilson & Dr. Lisa Park',
        authorTitle: 'Microbiome Research Team, Ukweli Institute',
        authorImage: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        image: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
        videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
        excerpt: 'Our most-watched webinar featuring leading experts discussing what the science really says about probiotics and their impact on health outcomes.',
        topics: ['Probiotics', 'Gut Health'],
        duration: '48:22',
        views: '24,876',
        likes: '1,243',
        description: 'In this in-depth webinar, Dr. James Wilson and Dr. Lisa Park cut through the marketing hype surrounding probiotics to reveal what the scientific evidence actually supports. They explore the most well-researched probiotic strains, discuss the importance of strain-specific effects, and analyze the current state of clinical evidence for various health claims.\n\nThis session covers:\n- How probiotics work in the gut ecosystem\n- The strongest evidence-based applications for probiotics\n- Common misconceptions in probiotic marketing\n- How to evaluate probiotic products for quality and efficacy\n- The future of next-generation probiotics and personalized approaches',
        transcript: [{
          time: '00:00',
          speaker: 'Dr. James Wilson',
          text: "Welcome everyone to this Ukweli Talks session on probiotics. I'm Dr. James Wilson, joined today by my colleague Dr. Lisa Park. We're going to be diving deep into what the science really tells us about probiotics, beyond all the marketing hype you might encounter."
        }, {
          time: '01:15',
          speaker: 'Dr. Lisa Park',
          text: "Thanks James. Before we get into specific products and claims, I think it's important to establish what probiotics actually are. By definition, probiotics are live microorganisms that, when administered in adequate amounts, confer a health benefit on the host. This definition has several important components that we'll unpack throughout this talk."
        }, {
          time: '02:30',
          speaker: 'Dr. James Wilson',
          text: 'Exactly, Lisa. And one of the most critical points is that probiotic effects are strain-specific. This means that different strains, even within the same species, can have completely different effects on the body. This is often overlooked in marketing materials, which might just mention a genus and species like Lactobacillus acidophilus without specifying the exact strain.'
        }, {
          time: '03:45',
          speaker: 'Dr. Lisa Park',
          text: "That's right. And this strain specificity is crucial when we look at the evidence. For example, Lactobacillus rhamnosus GG has been well-studied for preventing antibiotic-associated diarrhea, but other L. rhamnosus strains might not have the same effect. The research is tied to specific strains for specific conditions."
        }, {
          time: '05:10',
          speaker: 'Dr. James Wilson',
          text: "Let's talk about what conditions have the strongest evidence for probiotic benefits. The most robust evidence exists for antibiotic-associated diarrhea, certain forms of infectious diarrhea, and some specific digestive conditions like certain subtypes of IBS."
        }, {
          time: '06:30',
          speaker: 'Dr. Lisa Park',
          text: "And it's worth noting that for many other commonly advertised benefits, the evidence is either preliminary, mixed, or sometimes nonexistent. Claims about probiotics improving immunity, mental health, skin conditions, or weight management often get ahead of what's been proven in rigorous clinical trials."
        }]
      }, {
        id: 'microbiome-explained',
        title: 'Microbiome Research Explained: Interview with Azn Alahah',
        type: 'Video',
        date: 'July 28, 2023',
        author: 'Ukweli Media Team',
        authorTitle: 'Ukweli Institute',
        authorImage: 'https://uploadthingy.s3.us-west-1.amazonaws.com/pHWegwvKPXZhGe1ZwtEHkB/Generated_Image_September_17%2C_2025_-_8_14PM~2.png',
        image: 'https://images.unsplash.com/photo-1551817958-d9d86fb29431?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
        videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
        excerpt: 'Leading researcher Azn Alahah breaks down complex microbiome concepts into accessible insights.',
        topics: ['Microbiome', 'Research Methods'],
        duration: '36:45',
        views: '18,452',
        likes: '987',
        description: 'In this enlightening interview, Azn Alahah, Founder and Chief Scientific Officer of the Ukweli Institute, explains the fundamentals of microbiome research and its implications for human health. With his extensive background in microbiome science, Alahah makes complex scientific concepts accessible to a general audience.\n\nThis interview covers:\n- The basic composition of the human microbiome\n- How modern research techniques are revolutionizing our understanding\n- The connection between microbiome diversity and health outcomes\n- Common misconceptions about microbiome science\n- Promising directions for future research and therapeutic applications',
        transcript: [{
          time: '00:00',
          speaker: 'Interviewer',
          text: "Today we're speaking with Azn Alahah, Founder and Chief Scientific Officer of the Ukweli Institute. Mr. Alahah, thank you so much for joining us."
        }, {
          time: '00:15',
          speaker: 'Azn Alahah',
          text: "Thank you for having me. I'm excited to share some insights about microbiome research and why it matters for human health."
        }, {
          time: '00:30',
          speaker: 'Interviewer',
          text: "Let's start with the basics. When we talk about the human microbiome, what exactly are we referring to?"
        }, {
          time: '00:45',
          speaker: 'Azn Alahah',
          text: "The human microbiome refers to the entire collection of microorganisms that live in and on our bodies—bacteria, fungi, viruses, and other microbes. These communities exist throughout the body, but the largest and most diverse is in our gut. What's fascinating is that we have more microbial cells in our body than human cells, and they contain over 100 times more genes than our human genome."
        }]
      }, {
        id: 'fermented-foods',
        title: 'Ukweli Insights: The Science of Fermented Foods',
        type: 'Video',
        date: 'January 25, 2023',
        author: 'Dr. Lisa Park',
        authorTitle: 'Nutritional Microbiology Lead, Ukweli Institute',
        authorImage: 'https://images.unsplash.com/photo-1551836022-d5d88e9218df?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        image: 'https://images.unsplash.com/photo-1563355846-0e8ad5491037?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
        excerpt: 'Exploring the microbiological processes and health benefits of traditional fermented foods.',
        topics: ['Nutrition', 'Microbiome'],
        duration: '28:42',
        views: '12,783',
        likes: '756',
        description: 'In this educational video, Dr. Lisa Park explores the fascinating world of fermented foods and their potential impact on gut health and overall wellness. From ancient traditions to modern science, this presentation covers the microbiological processes behind fermentation and the evidence for health benefits.\n\nTopics covered include:\n- The history and cultural significance of fermented foods across different societies\n- The microbiology of fermentation and how beneficial microbes transform food\n- Research on the potential health benefits of kimchi, sauerkraut, kefir, kombucha, and other fermented foods\n- How fermented foods differ from probiotic supplements\n- Practical guidance on incorporating fermented foods into your diet',
        transcript: [{
          time: '00:00',
          speaker: 'Dr. Lisa Park',
          text: "Hello everyone, and welcome to this Ukweli Insights presentation on the science of fermented foods. I'm Dr. Lisa Park, and today we'll explore how these traditional foods are made, how they affect our microbiome, and what the research says about their health benefits."
        }, {
          time: '01:20',
          speaker: 'Dr. Lisa Park',
          text: 'Fermented foods have been part of human diets for thousands of years, across virtually every culture on the planet. From sauerkraut in Eastern Europe to kimchi in Korea, tempeh in Indonesia to kefir in the Caucasus Mountains, humans have been harnessing microbial fermentation long before we understood the science behind it.'
        }]
      }];
      const foundVideo = mockVideos.find(v => v.id === videoId);
      // Find related videos (excluding the current one)
      const related = mockVideos.filter(v => v.id !== videoId && (foundVideo ? v.topics.some(topic => foundVideo.topics.includes(topic)) : false)).slice(0, 3);
      setTimeout(() => {
        setVideo(foundVideo || null);
        setRelatedVideos(related);
        setLoading(false);
      }, 800); // Simulate network delay
    };
    fetchVideo();
  }, [videoId]);
  if (loading) {
    return <div className="min-h-screen pt-32 pb-12 flex flex-col items-center justify-start bg-gray-50" data-id="element-2165" data-mp="37oshmhj7juo9">
        <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" data-id="element-2166" data-mp="2jyofkelo42zd"></div>
        <p className="mt-4 text-gray-600" data-id="element-2167" data-mp="2f1g6d3ihz9a9">Loading video...</p>
      </div>;
  }
  if (!video) {
    return <div className="min-h-screen pt-32 pb-12 flex flex-col items-center justify-start bg-gray-50" data-id="element-2168" data-mp="2tz8kpscb5rmv">
        <div className="text-center" data-id="element-2169" data-mp="2owzpepbyqdhl">
          <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-2170" data-mp="97ete8zn3yia">
            Video Not Found
          </h2>
          <p className="text-gray-600 mb-8" data-id="element-2171" data-mp="48lpn8a5nlvb">
            The video you're looking for doesn't exist or has been removed.
          </p>
          <Link to="/media" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" data-id="element-2172" data-mp="baiad8ydivxk">
            <ChevronLeftIcon size={16} className="mr-2" data-id="element-2173" data-mp="l9g4crekjhny" />
            Back to Media & Insights
          </Link>
        </div>
      </div>;
  }
  return <div className="bg-white w-full" data-id="element-2174" data-mp="q6q0epwgqrkr">
      {/* Hero Section */}
      <section className="relative pt-32 pb-12 bg-gray-50" data-id="element-2175" data-mp="3orr8t6zl5xss">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2176" data-mp="3tp9kh54zmr4h">
          <div className="mb-6" data-id="element-2177" data-mp="3tnezxrxmp10x">
            <Link to="/media" className="inline-flex items-center text-blue-600 hover:text-blue-800" data-id="element-2178" data-mp="2jhwoceonqku">
              <ChevronLeftIcon size={16} className="mr-1" data-id="element-2179" data-mp="7bhsptrmtzc5" />
              Back to Media & Insights
            </Link>
          </div>
        </div>
      </section>
      {/* Video Player Section */}
      <section className="bg-gray-900 py-12" data-id="element-2180" data-mp="3axqgow35391e">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2181" data-mp="1s3x2d0xwwh03">
          <div className="aspect-w-16 aspect-h-9 rounded-lg overflow-hidden shadow-2xl" data-id="element-2182" data-mp="1s3464twkx9w7">
            <iframe src={video.videoUrl} title={video.title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" data-id="element-2183" data-mp="1o2tnohrlor0g"></iframe>
          </div>
        </div>
      </section>
      {/* Video Info Section */}
      <section className="py-8 bg-white border-b border-gray-200" data-id="element-2184" data-mp="14e2ruq12f4j0">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2185" data-mp="10cr0og2jmb8p">
          <div className="flex flex-wrap items-center gap-2 mb-3" data-id="element-2186" data-mp="10d97u7qqu9ve">
            {video.topics.map(topic => <span key={topic} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-2187" data-mp="vbjswh0ehc21">
                <TagIcon size={12} className="mr-1" data-id="element-2188" data-mp="lg8lykv8h94u" />
                {topic}
              </span>)}
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4" data-id="element-2189" data-mp="2qgt7cno43u84">
            {video.title}
          </h1>
          <div className="flex flex-wrap justify-between items-center mb-6" data-id="element-2190" data-mp="2bsg91k1jxzag">
            <div className="flex items-center" data-id="element-2191" data-mp="2gqzwxpzphl5g">
              <img src={video.authorImage} alt={video.author} className="w-10 h-10 rounded-full mr-3 object-cover" data-id="element-2192" data-mp="2gpoubthzyjfx" />
              <div className="p-6" data-id="element-2193" data-mp="27qlagnb0a00d">
                <h3 className="text-xl font-semibold text-gray-900" data-id="element-2194" data-mp="27rw9yaxf4llo">
                  {video.author.includes('Azn Alahah') ? <Link to="/profile" className="hover:text-blue-600 transition-colors" data-id="element-2195" data-mp="1xs5heesf4go8">
                      {video.author}
                    </Link> : video.author}
                </h3>
                <p className="text-blue-600 mb-3" data-id="element-2196" data-mp="1gijn2s86qmsi">{video.authorTitle}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4 mt-4 sm:mt-0" data-id="element-2197" data-mp="1bk8d8hhw4caa">
              <div className="flex items-center text-gray-600" data-id="element-2198" data-mp="1q8mg2sfb40qu">
                <CalendarIcon size={16} className="mr-1" data-id="element-2199" data-mp="1qa7a8gxplho6" />
                <span className="text-sm" data-id="element-2200" data-mp="1lhe69va2edd5">{video.date}</span>
              </div>
              <div className="flex items-center text-gray-600" data-id="element-2201" data-mp="1z9aydzbqgn9j">
                <ClockIcon size={16} className="mr-1" data-id="element-2202" data-mp="1z7ho8a1wvkws" />
                <span className="text-sm" data-id="element-2203" data-mp="1v8h6yvz30qbw">{video.duration}</span>
              </div>
              <div className="flex items-center text-gray-600" data-id="element-2204" data-mp="2978hk8eqjtva">
                <PlayIcon size={16} className="mr-1" data-id="element-2205" data-mp="295e7jp505m5z" />
                <span className="text-sm" data-id="element-2206" data-mp="248477eg2rq3d">{video.views} views</span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap justify-between items-center py-4 border-t border-b border-gray-200" data-id="element-2207" data-mp="eu8zm8mvunku">
            <div className="flex space-x-4" data-id="element-2208" data-mp="9w7dtfy2oa5o">
              <button className="inline-flex items-center text-gray-700 hover:text-blue-600 transition-colors" data-id="element-2209" data-mp="29wvx5p1tddw1">
                <ThumbsUpIcon size={18} className="mr-2" data-id="element-2210" data-mp="2euxd413y0x3a" />
                <span data-id="element-2211" data-mp="2exjustkjssz8">{video.likes}</span>
              </button>
              <button className="inline-flex items-center text-gray-700 hover:text-blue-600 transition-colors" data-id="element-2212" data-mp="2iwiyybygglpm">
                <ShareIcon size={18} className="mr-2" data-id="element-2213" data-mp="2nxoro9s488z5" />
                Share
              </button>
              <button className="inline-flex items-center text-gray-700 hover:text-blue-600 transition-colors" data-id="element-2214" data-mp="2sv8557gzohq1">
                <BookmarkIcon size={18} className="mr-2" data-id="element-2215" data-mp="2xsgnp961unrt" />
                Save
              </button>
            </div>
            <div className="mt-4 sm:mt-0" data-id="element-2216" data-mp="36mvbk04nb6mj">
              <button className="inline-flex items-center text-gray-700 hover:text-blue-600 transition-colors" onClick={() => setShowTranscript(!showTranscript)} data-id="element-2217" data-mp="36llcb7m23seb">
                <FileTextIcon size={18} className="mr-2" data-id="element-2218" data-mp="3glkk32anjeq4" />
                {showTranscript ? 'Hide Transcript' : 'Show Transcript'}
              </button>
            </div>
          </div>
        </div>
      </section>
      {/* Video Description and Transcript */}
      <section className="py-8 bg-white" data-id="element-2219" data-mp="3gqa5ftzkmgtu">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2220" data-mp="3grbp2munkbn6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2221" data-mp="3cq01snz942i5">
            <div className="md:col-span-2" data-id="element-2222" data-mp="da3bif6yjxy9">
              <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-2223" data-mp="d8rx7lfvz7sw">
                About this video
              </h2>
              <div className="text-gray-700 whitespace-pre-line mb-8" data-id="element-2224" data-mp="8bs2u7sefthm">
                {video.description}
              </div>
              {showTranscript && <div data-id="element-2225" data-mp="3vovs3o7znwqp">
                  <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-2226" data-mp="3vnkphrqa4v16">
                    Transcript
                  </h2>
                  <div className="bg-gray-50 rounded-lg p-6" data-id="element-2227" data-mp="w2p50sue7cfo">
                    {video.transcript.map((segment, index) => <div key={index} className="mb-6" data-id="element-2228" data-mp="1ay6b6ra1p2dq" data-mpi={index}>
                        <div className="flex items-center mb-2" data-id="element-2229" data-mp="1awli763dprhz">
                          <span className="text-sm font-mono text-gray-500 mr-3" data-id="element-2230" data-mp="1fpocjgfzd4z8">
                            {segment.time}
                          </span>
                          <span className="font-medium text-gray-900" data-id="element-2231" data-mp="15ytezfxaaiyh">
                            {segment.speaker === 'Azn Alahah' ? <Link to="/profile" className="hover:text-blue-600 transition-colors" data-id="element-2232" data-mp="1tpfm94zgrz7f">
                                {segment.speaker}
                              </Link> : segment.speaker}
                          </span>
                        </div>
                        <p className="text-gray-700 pl-16" data-id="element-2233" data-mp="8de5zgukuoce">{segment.text}</p>
                      </div>)}
                  </div>
                </div>}
            </div>
            <div className="md:col-span-1" data-id="element-2234" data-mp="2miswe28cc8tg">
              <div className="bg-gray-50 rounded-lg p-6" data-id="element-2235" data-mp="2mhraf2pse1wy">
                <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-2236" data-mp="30ilgvft9xssz">
                  Resources
                </h3>
                <ul className="space-y-3" data-id="element-2237" data-mp="2wggu9118twfu">
                  <li data-id="element-2238" data-mp="2whruwrze6o2q">
                    <a href="#" className="flex items-center text-blue-600 hover:text-blue-800" data-id="element-2239" data-mp="2wf5qv2bpmqp9">
                      <DownloadIcon size={16} className="mr-2" data-id="element-2240" data-mp="23tw1bw54dca6" />
                      Download presentation slides
                    </a>
                  </li>
                  <li data-id="element-2241" data-mp="2cshgrifaob6q">
                    <a href="#" className="flex items-center text-blue-600 hover:text-blue-800" data-id="element-2242" data-mp="2cry7eq3pmv4b">
                      <FileTextIcon size={16} className="mr-2" data-id="element-2243" data-mp="1l237jzi87n1e" />
                      Related research paper
                    </a>
                  </li>
                  <li data-id="element-2244" data-mp="f46xr8xniu8w">
                    <a href="#" className="flex items-center text-blue-600 hover:text-blue-800" data-id="element-2245" data-mp="f1b1dp1gcdmk">
                      <DownloadIcon size={16} className="mr-2" data-id="element-2246" data-mp="ou8ok8he2qri" />
                      Download transcript (PDF)
                    </a>
                  </li>
                </ul>
              </div>
              <div className="mt-6 bg-blue-50 rounded-lg p-6" data-id="element-2247" data-mp="yqbod7orqdba">
                <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-2248" data-mp="tstn83gzt2du">
                  Share this video
                </h3>
                <div className="flex space-x-3 mb-4" data-id="element-2249" data-mp="17r9x23a5j907">
                  <a href="#" className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" data-id="element-2250" data-mp="12t8d7g4wj5no">
                    <FacebookIcon size={18} data-id="element-2251" data-mp="39lsdt5vqmma1" />
                  </a>
                  <a href="#" className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" data-id="element-2252" data-mp="34lwkq1okwf99">
                    <TwitterIcon size={18} data-id="element-2253" data-mp="1p1wzpmk0nb9y" />
                  </a>
                  <a href="#" className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" data-id="element-2254" data-mp="1p3hu9c6byu7t">
                    <LinkedinIcon size={18} data-id="element-2255" data-mp="1fa13kh1hpv8d" />
                  </a>
                  <a href="#" className="p-2 bg-gray-700 text-white rounded-full hover:bg-gray-800 transition-colors" data-id="element-2256" data-mp="11b1b0yauww6x">
                    <MailIcon size={18} data-id="element-2257" data-mp="sbwd9u74d6vt" />
                  </a>
                </div>
                <div className="relative" data-id="element-2258" data-mp="wcq2cqwasrqa">
                  <input type="text" value={`https://ukweli.org/video/${video.id}`} readOnly className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-500" data-id="element-2259" data-mp="wdrg534p7sbp" />
                  <button className="absolute right-2 top-1/2 transform -translate-y-1/2 text-blue-600 hover:text-blue-800 text-sm font-medium" data-id="element-2260" data-mp="3jmizjgaitb0">
                    Copy
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Related Videos */}
      {relatedVideos.length > 0 && <section className="py-12 bg-gray-50" data-id="element-2261" data-mp="m91gx50oj2b6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2262" data-mp="m7rlknl7o888">
            <h2 className="text-2xl font-bold text-gray-900 mb-8" data-id="element-2263" data-mp="i6xx9t3uwret">
              Related Videos
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2264" data-mp="dfpkcn3binww">
              {relatedVideos.map(related => <motion.div key={related.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300" data-id="element-2265" data-mp="8h4w7m1cbuot">
                  <Link to={`/video/${related.id}`} className="block h-48 overflow-hidden relative" data-id="element-2266" data-mp="1iecp22fy230j">
                    <img src={related.image} alt={related.title} className="w-full h-full object-cover transition-transform duration-300 hover:scale-105" data-id="element-2267" data-mp="1rh438a07fcvv" />
                    <div className="absolute inset-0 flex items-center justify-center" data-id="element-2268" data-mp="216xd96kp7jy0">
                      <div className="bg-blue-600/80 rounded-full p-3 text-white" data-id="element-2269" data-mp="264p8oakjxo6y">
                        <PlayIcon size={24} data-id="element-2270" data-mp="2a850mtlof0db" />
                      </div>
                    </div>
                    <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded" data-id="element-2271" data-mp="2f6opb1rnmq9d">
                      {related.duration}
                    </div>
                  </Link>
                  <div className="p-6" data-id="element-2272" data-mp="1xgtcihkv39h3">
                    <div className="flex items-center text-sm text-gray-500 mb-2" data-id="element-2273" data-mp="1xfrwrzswhyt1">
                      <CalendarIcon size={14} className="mr-1" data-id="element-2274" data-mp="2bb3sa9evrtb0" />
                      <span data-id="element-2275" data-mp="26ay0mt8m26sx">{related.date}</span>
                    </div>
                    <Link to={`/video/${related.id}`} data-id="element-2276" data-mp="1ijsjmaqxp8h1">
                      <h3 className="font-bold text-lg mb-2 text-gray-900 hover:text-blue-600 transition-colors" data-id="element-2277" data-mp="1imeog2mfx9vk">
                        {related.title}
                      </h3>
                    </Link>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2" data-id="element-2278" data-mp="1sgf4gzj9fzj2">
                      {related.excerpt}
                    </p>
                    <Link to={`/video/${related.id}`} className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium text-sm" data-id="element-2279" data-mp="33wvluyedkq8k">
                      Watch video <ArrowRightIcon size={16} className="ml-1" data-id="element-2280" data-mp="kq1bype317b8" />
                    </Link>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>}
      {/* Comments Section - could be implemented in a real app */}
      <section className="py-12 bg-white" data-id="element-2281" data-mp="gq8ypzdquczj">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2282" data-mp="grbpm7b2gffg">
          <div className="flex items-center mb-8" data-id="element-2283" data-mp="14g3b6fu5hzj9">
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-2284" data-mp="18gdpqgw0dy9q">Comments</h2>
            <div className="ml-3 bg-gray-100 text-gray-700 text-sm px-2 py-1 rounded" data-id="element-2285" data-mp="18iht8xpzwv9h">
              24
            </div>
          </div>
          <div className="flex items-start mb-8" data-id="element-2286" data-mp="zntln0wy71hi">
            <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80" alt="Your profile" className="w-10 h-10 rounded-full mr-4 object-cover" data-id="element-2287" data-mp="zm8ttj20pwnc" />
            <div className="flex-grow" data-id="element-2288" data-mp="220mpjcc7pify">
              <textarea placeholder="Add a comment..." className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" rows={3} data-id="element-2289" data-mp="21zlcx3fjsnw4"></textarea>
              <div className="mt-2 flex justify-end" data-id="element-2290" data-mp="1n3lwn5iabhfw">
                <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" data-id="element-2291" data-mp="1n56rkw8ih2ea">
                  Comment
                </button>
              </div>
            </div>
          </div>
          <div className="space-y-8" data-id="element-2292" data-mp="19bp81bftrpo9">
            {/* Sample comments - would be dynamic in a real app */}
            <div className="flex items-start" data-id="element-2293" data-mp="14cuqectlg75q">
              <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80" alt="Commenter" className="w-10 h-10 rounded-full mr-4 object-cover" data-id="element-2294" data-mp="14e5u6cn1hewu" />
              <div data-id="element-2295" data-mp="36zt2fgoos7kf">
                <div className="flex items-center mb-1" data-id="element-2296" data-mp="36y89twlxmyp7">
                  <h4 className="font-medium text-gray-900 mr-2" data-id="element-2297" data-mp="3b0v5y9pmz5s7">
                    Elena Martinez
                  </h4>
                  <span className="text-sm text-gray-500" data-id="element-2298" data-mp="3fzogpudjgioo">2 days ago</span>
                </div>
                <p className="text-gray-700 mb-2" data-id="element-2299" data-mp="3kzav4753s4z2">
                  This was incredibly informative! I've been taking probiotics
                  for years without really understanding the strain-specific
                  effects. Would love to see more content about how to evaluate
                  probiotic products.
                </p>
                <div className="flex items-center text-sm" data-id="element-2300" data-mp="2x85gtwet9kqv">
                  <button className="text-gray-500 hover:text-gray-700 mr-4" data-id="element-2301" data-mp="320gip4t43gm2">
                    <ThumbsUpIcon size={14} className="inline mr-1" data-id="element-2302" data-mp="24l2ndiozs07h" /> 12
                  </button>
                  <button className="text-gray-500 hover:text-gray-700" data-id="element-2303" data-mp="29jm4n5sn4nti">
                    Reply
                  </button>
                </div>
              </div>
            </div>
            <div className="flex items-start" data-id="element-2304" data-mp="xxgobdgs6046">
              <img src="https://images.unsplash.com/photo-1599566150163-29194dcaad36?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80" alt="Commenter" className="w-10 h-10 rounded-full mr-4 object-cover" data-id="element-2305" data-mp="xys308brksa2" />
              <div data-id="element-2306" data-mp="1lv5sw8cmppdy">
                <div className="flex items-center mb-1" data-id="element-2307" data-mp="1ludz932le1qb">
                  <h4 className="font-medium text-gray-900 mr-2" data-id="element-2308" data-mp="1lt2zdec9pe4h">
                    Michael Chen
                  </h4>
                  <span className="text-sm text-gray-500" data-id="element-2309" data-mp="1uo96cgn9y28l">3 days ago</span>
                </div>
                <p className="text-gray-700 mb-2" data-id="element-2310" data-mp="1qme2b4dylar4">
                  Dr. Wilson and Dr. Park explain complex concepts so clearly. I
                  appreciate the honesty about where the evidence is strong vs.
                  where more research is needed. This is the kind of science
                  communication we need!
                </p>
                <div className="flex items-center text-sm" data-id="element-2311" data-mp="2j9dveh0wngmd">
                  <button className="text-gray-500 hover:text-gray-700 mr-4" data-id="element-2312" data-mp="2o7yw0hk2k2bc">
                    <ThumbsUpIcon size={14} className="inline mr-1" data-id="element-2313" data-mp="29aon9fyt9ajb" /> 8
                  </button>
                  <button className="text-gray-500 hover:text-gray-700" data-id="element-2314" data-mp="2e99dcmkcmdu1">
                    Reply
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-8 text-center" data-id="element-2315" data-mp="258ky5oj7y7fk">
            <button className="text-blue-600 hover:text-blue-800 font-medium" data-id="element-2316" data-mp="259mm2tlc2oep">
              Load more comments
            </button>
          </div>
        </div>
      </section>
      {/* Newsletter Sign Up */}
      <section className="py-12 bg-gray-50" data-id="element-2317" data-mp="1vjjgzv0xtk00">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2318" data-mp="1hjikssvmb0qv">
          <div className="bg-blue-600 rounded-xl p-8 md:p-12 text-center" data-id="element-2319" data-mp="1hld648aswuvh">
            <h2 className="text-2xl font-bold text-white mb-4" data-id="element-2320" data-mp="1ljkdicjdkju0">
              Subscribe to Our Channel
            </h2>
            <p className="text-blue-100 mb-6" data-id="element-2321" data-mp="8knkjvwo8vh0">
              Get notified about new videos, webinars, and educational content
              from the Ukweli Institute
            </p>
            <div className="max-w-md mx-auto" data-id="element-2322" data-mp="ifffst2s768m">
              <form className="flex flex-col sm:flex-row gap-2" data-id="element-2323" data-mp="nftpefk16o8r">
                <input type="email" placeholder="Your email address" className="flex-grow px-4 py-2 border border-transparent rounded-md focus:ring-white focus:border-white" required data-id="element-2324" data-mp="nhegtwb1tr2e" />
                <button type="submit" className="bg-white text-blue-600 px-6 py-2 rounded-md hover:bg-blue-50 transition-colors whitespace-nowrap font-medium" data-id="element-2325" data-mp="118t44fbzoef9">
                  Subscribe
                </button>
              </form>
              <p className="text-xs text-blue-100 mt-2" data-id="element-2326" data-mp="1f6hlvearf5g3">
                We respect your privacy. Unsubscribe at any time.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>;
};